'use client';

import { INTERNAL_CMS_MEDIA_COLLECTION } from '@/constants/internal-cms-media';
import { useTenantSelection } from '@payloadcms/plugin-multi-tenant/client';
import { useConfig, useUploadHandlers } from '@payloadcms/ui';
import { formatAdminURL } from 'payload/shared';
import { Fragment, useEffect } from 'react';

import { uploadToCloudinary } from './upload-to-cloudinary';
import { validateBeforeUpload } from './validate-in-browser';

import type { UploadCollectionSlug } from 'payload';
import type { ReactNode } from 'react';

interface Props {
  children: ReactNode;
  collectionSlug: UploadCollectionSlug;
  enabled?: boolean;
  serverHandlerPath: `/${string}`;
}

/**
 * Registers the browser-side upload handler. Renders no UI of its own.
 *
 * Payload mounts this as an admin provider, wrapping the whole panel, and its only job is to drop a
 * function into `useUploadHandlers()`. The form calls that function on save.
 *
 * It replaces Payload's `createClientUploadHandler` factory, which wraps the handler in *its* own
 * component and so leaves no place to call a hook. We need `useTenantSelection()` to know which
 * Landing the file belongs to.
 */
export const CloudinaryClientUploadHandler = ({ children, collectionSlug, enabled, serverHandlerPath }: Props): React.JSX.Element => {
  const { setUploadHandler } = useUploadHandlers();
  const { selectedTenantID } = useTenantSelection();
  const {
    config: {
      routes: { api: apiRoute },
      serverURL,
    },
  } = useConfig();

  useEffect(() => {
    if (!enabled) return;

    setUploadHandler({
      collectionSlug,
      handler: async ({ file }) => {
        const mimeType = await validateBeforeUpload(file);

        return uploadToCloudinary({
          collectionSlug,
          file,
          mimeType,
          needsTenant: collectionSlug !== INTERNAL_CMS_MEDIA_COLLECTION,
          signUrl: formatAdminURL({ apiRoute, path: serverHandlerPath, serverURL }),
          tenantId: selectedTenantID,
        });
      },
    });
  }, [apiRoute, collectionSlug, enabled, selectedTenantID, serverHandlerPath, serverURL, setUploadHandler]);

  return <Fragment>{children}</Fragment>;
};
